import ExtractByTypename from 'graphql-client/ExtractByTypename';
import * as net from 'util/Net';
import {OngoingTaskResponse} from 'util/TaskRunner';

import {BenchlingAppGraphQL} from '../../graphql-fragments/benchling-apps/__generated/BenchlingAppGraphQL';
import {getBenchlingAppConfigurationQuery} from './configuration/__generated/getBenchlingAppConfigurationQuery';

type Configuration = ExtractByTypename<
  getBenchlingAppConfigurationQuery['benchlingApp'],
  'BenchlingAppNode'
>['configuration'][number];

export const BENCHLING_APPS_API_URL = '/apps';
export const BENCHLING_APP_DEFINITION_API_URL = '/app-definitions';
export const BENCHLING_APP_INSTANCES_API_URL = '/app-instances';
export const BENCHLING_APPS_WEB_URL = '/developer/apps';
export const CANVAS_BUILDER_URL = '/developer/app-canvas-builder';
// TODO(BNCH-65816): Remove client-side default app-version handling in after backfilling app version to all apps.
export const DEFAULT_APP_VERSION = '0.0.1';

// TODO - import instead from __generated/graphqlGlobalTypes after graphQL changes land
// https://github.com/benchling/aurelia/pull/100284
export enum Audience {
  PRIVATE = 'private',
  PUBLIC = 'public',
}

export interface UpdateBenchlingAppDefinitionArgs {
  webhookUrl?: string | null;
  publicKey?: string | null;
  webhookHeaders?: Array<{key: string; value: string}> | null;
}

export interface UpdateBenchlingAppDefinitionVersionArgs {
  name?: string;
  description?: string;
  allowList?: Array<string>;
}

export interface UpdateBenchlingAppDefinitionVersionFromManifestArgs {
  location: string;
}

export interface UpdateBenchlingAppInstanceArgs {
  configuration: Array<Configuration>;
  resourceFeatureAssociations: Array<{feature: number; resources: Array<string>}>;
}

export interface CreateBenchlingAppArgs {
  name: string;
  description: string;
  audience: Audience;
}
// TODO (BNCH-92479): remove this when we migrate all legacy apps
export interface CreateBenchlingAppArgsLegacy {
  name: string;
  description: string;
  lifecycleManagement: string;
  webhookUrl: string | null;
}

export interface CreateBenchlingAppFromManifestArgs {
  location: string;
  audience: Audience;
}

// TODO (BNCH-92479): remove this when we migrate all legacy apps
export interface CreateBenchlingAppFromManifestArgsLegacy {
  location: string;
}

export function deprecatedUpdateBenchlingAppDefinition(
  benchlingAppId: string,
  data: UpdateBenchlingAppDefinitionArgs
): Promise<{benchlingApp: BenchlingAppGraphQL}> {
  return net.patchJSONPromise({
    url: `/1/api${BENCHLING_APPS_API_URL}/${benchlingAppId}`,
    data,
  });
}

export function deprecatedUpdateBenchlingAppDefinitionVersion(
  benchlingAppId: string,
  version: string | null,
  data: UpdateBenchlingAppDefinitionVersionArgs
): Promise<{benchlingApp: BenchlingAppGraphQL}> {
  return net.patchJSONPromise({
    url: `/1/api${BENCHLING_APPS_API_URL}/${benchlingAppId}/versions/${version ?? DEFAULT_APP_VERSION}`,
    data,
  });
}

export function deprecatedUpdateBenchlingAppDefinitionVersionFromManifest(
  benchlingAppId: string,
  version: string | null,
  data: UpdateBenchlingAppDefinitionVersionFromManifestArgs
): Promise<{benchlingApp: BenchlingAppGraphQL}> {
  return net.patchJSONPromise({
    url: `/1/api${BENCHLING_APPS_API_URL}/${benchlingAppId}/versions/${
      version ?? DEFAULT_APP_VERSION
    }/manifest`,
    data,
  });
}

export function updateBenchlingAppInstance(
  benchlingAppInstanceId: string,
  data: UpdateBenchlingAppInstanceArgs
): Promise<{benchlingApp: BenchlingAppGraphQL}> {
  return net.patchJSONPromise({
    url: `/1/api${BENCHLING_APP_INSTANCES_API_URL}/${benchlingAppInstanceId}`,
    data,
  });
}

export function deprecatedCreateBenchlingApp(
  data: CreateBenchlingAppArgs
): Promise<{benchlingApp: BenchlingAppGraphQL}> {
  return net.postJSONPromise({
    url: `/1/api${BENCHLING_APPS_API_URL}`,
    data,
  });
}

// TODO (BNCH-92479): remove this when we migrate all legacy apps
export function createBenchlingAppLegacy(
  data: CreateBenchlingAppArgsLegacy
): Promise<{benchlingApp: BenchlingAppGraphQL}> {
  return net.postJSONPromise({
    url: `/1/api${BENCHLING_APPS_API_URL}`,
    data,
  });
}

export function deprecatedCreateBenchlingAppFromManifest(
  data: CreateBenchlingAppFromManifestArgs
): Promise<{benchlingApp: BenchlingAppGraphQL}> {
  return net.postJSONPromise({
    url: `/1/api${BENCHLING_APPS_API_URL}/manifest`,
    data,
  });
}

// TODO (BNCH-92479): remove this when we migrate all legacy apps
export function createBenchlingAppFromManifestLegacy(
  data: CreateBenchlingAppFromManifestArgsLegacy
): Promise<{benchlingApp: BenchlingAppGraphQL}> {
  return net.postJSONPromise({
    url: `/1/api${BENCHLING_APPS_API_URL}/manifest`,
    data,
  });
}

interface BulkArchiveResponse {
  archivedItemIds: Array<string>;
}
export function bulkArchiveBenchlingApps(ids: Array<string>, purpose: string): Promise<BulkArchiveResponse> {
  return net.postJSONPromise({
    url: `/1/api${BENCHLING_APPS_API_URL}:archive`,
    data: {appIds: ids, reason: purpose},
  });
}

interface BulkUnarchiveResponse {
  unarchivedItemIds: Array<string>;
}
export function bulkUnarchiveBenchlingApps(ids: Array<string>): Promise<BulkUnarchiveResponse> {
  return net.postJSONPromise({
    url: `/1/api${BENCHLING_APPS_API_URL}:unarchive`,
    data: {appIds: ids},
  });
}

export function activateBenchlingApp(BenchlingAppId: String): Promise<OngoingTaskResponse> {
  const response = net.postJSONPromise({
    url: `/1/api${BENCHLING_APPS_API_URL}/${BenchlingAppId}/lifecycle/activate`,
  });
  return response;
}

export function deactivateBenchlingApp(BenchlingAppId: String): Promise<OngoingTaskResponse> {
  const response = net.postJSONPromise({
    url: `/1/api${BENCHLING_APPS_API_URL}/${BenchlingAppId}/lifecycle/deactivate`,
  });
  return response;
}

interface CheckLifecycleChangeTaskResponse {
  result?: {deliveryStatus: 'success' | 'failure'};
  status: string;
}

export function checkLifecycleChangeTask(taskStatusUrl: string): Promise<CheckLifecycleChangeTaskResponse> {
  return net.getJSONPromise(taskStatusUrl);
}

export function regenerateClientSecret(benchlingAppId: string): Promise<{clientSecret: string}> {
  return net.postJSONPromise({
    url: `/1/api${BENCHLING_APPS_API_URL}/${benchlingAppId}/oauth2-client-secret`,
  });
}

export function deleteClientSecret(benchlingAppId: string): Promise<void> {
  return net.deletePromise({
    url: `/1/api${BENCHLING_APPS_API_URL}/${benchlingAppId}/oauth2-client-secret`,
  });
}

export function appURL(apiId: string): string {
  return `/apps/${apiId}`;
}

export function queryGlobalAppDefinition(benchlingAppId: string): Promise<{
  benchlingAppDefinition: {
    name: string;
    description: string;
    id: string;
    version: string;
  };
  benchlingAppInstance: {
    id: string;
  } | null;
  isLatest: boolean;
}> {
  return net.getJSONPromise(`/1/api${BENCHLING_APPS_API_URL}/global-definition/${benchlingAppId}`);
}

export function installFromGlobalAppDefinition(benchlingAppId: string): Promise<{
  benchlingApp: {
    id: string;
  };
}> {
  return net.postJSONPromise({
    url: `/1/api${BENCHLING_APPS_API_URL}/global-definition/${benchlingAppId}/install`,
  });
}

export function upgradeFromGlobalAppDefinition(benchlingAppId: string): Promise<{
  benchlingApp: {
    id: string;
  };
}> {
  return net.postJSONPromise({
    url: `/1/api${BENCHLING_APPS_API_URL}/global-definition/${benchlingAppId}/upgrade`,
  });
}

export function getWebhookMessageTypes(): Promise<{
  messageTypes: Array<string>;
}> {
  return net.getJSONPromise(`/1/api/webhook-message-types`);
}

export interface WebhookPlaygroundPayloadResponse {
  webhookUrl?: string;
  payload: Record<string, unknown>;
  variableProps: Array<string>;
}

export function getWebhookPayloadData(
  appDefinitionId: string,
  webhookType: string,
  webhookUrl: string | null
): Promise<WebhookPlaygroundPayloadResponse> {
  return net.postJSONPromise({
    url: `/1/api${BENCHLING_APP_DEFINITION_API_URL}/${appDefinitionId}/generate-example-payload`,
    data: {webhookType, ...(webhookUrl && {webhookUrl})},
  });
}

export function sendTestPayloadMessage(
  appDefinitionId: string,
  payload: Record<string, unknown>,
  webhookUrl: string
): Promise<{messageId: string}> {
  return net.postJSONPromise({
    url: `/1/api${BENCHLING_APP_DEFINITION_API_URL}/${appDefinitionId}/send-example-payload`,
    data: {payload, webhookUrl},
  });
}

export function verifyWebhookURL(webhookUrl: string): Promise<{}> {
  return net.postJSONPromise({
    url: `/1/api/webhook-url-verification`,
    data: {webhookUrl},
  });
}

export interface TenantInferredInfo {
  id: string;
  url: string | null;
}

export interface TenantInferredInfoError {
  error: string;
}

export function isTenantInferredInfoError(
  value: TenantInferredInfo | TenantInferredInfoError
): value is TenantInferredInfoError {
  return value.hasOwnProperty('error');
}

export interface GetInferredTenantInfoArgs {
  tenant_url_or_id: string;
}

export function getInferredTenantInfo(
  data: GetInferredTenantInfoArgs
): Promise<TenantInferredInfo | TenantInferredInfoError> {
  return net.postJSONPromise({
    url: `/1/api${BENCHLING_APPS_API_URL}/infer-tenant`,
    data,
  });
}
